import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Sprout, AlertTriangle, CheckCircle, Info, Map } from 'lucide-react';
import { FieldData, CropHealth, CropIssue } from './types';
import { cn } from '@/lib/utils';

const CROP_TYPES = [
  'Broiler Chickens',
  'Layer Chickens',
  'Ducks',
  'Rice',
  'Vegetables',
  'Fish',
  'Other',
];

const GROWTH_STAGES = [
  { id: 1, name: 'Germination', days: '0-15' },
  { id: 2, name: 'Vegetative', days: '15-45' },
  { id: 3, name: 'Flowering', days: '45-75' },
  { id: 4, name: 'Fruiting', days: '75-105' },
  { id: 5, name: 'Maturity', days: '105-135' },
];

interface CropMonitoringProps {
  field: FieldData;
  onFieldUpdate?: (field: Partial<FieldData>) => void;
}

export const CropMonitoring: React.FC<CropMonitoringProps> = ({ field, onFieldUpdate }) => {
  const { t } = useTranslation();
  const [cropType, setCropType] = useState(field.cropType || '');
  const [plantingDate, setPlantingDate] = useState(field.plantingDate || '');
  const [currentStage, setCurrentStage] = useState(1);
  const [health, setHealth] = useState<CropHealth>({
    overall: 'good',
    score: 75,
    trend: 'stable',
    issues: [],
  });

  useEffect(() => {
    // Calculate growth stage based on planting date
    if (plantingDate) {
      const planted = new Date(plantingDate);
      const now = new Date();
      const daysGrown = Math.floor((now.getTime() - planted.getTime()) / (1000 * 60 * 60 * 24));
      
      let stage = 1;
      if (daysGrown >= 105) stage = 5;
      else if (daysGrown >= 75) stage = 4;
      else if (daysGrown >= 45) stage = 3;
      else if (daysGrown >= 15) stage = 2;
      
      setCurrentStage(stage);
    }

    // Simulate anomaly detection
    detectAnomalies();
  }, [plantingDate]);

  const detectAnomalies = () => {
    // CBS-TODO: Wire to satellite anomaly detection API
    const issues: CropIssue[] = [];

    // Random chance of detecting issues
    if (Math.random() < 0.3) {
      issues.push({
        type: 'drought',
        severity: 'medium',
        description: 'Lower-than-normal moisture levels detected in northwest section',
        recommendation: 'Consider irrigation within 3-5 days',
      });
    }

    if (Math.random() < 0.2) {
      issues.push({
        type: 'pest',
        severity: 'low',
        description: 'Potential pest activity indicated by irregular growth patterns',
        recommendation: 'Schedule field inspection and consider preventive treatment',
      });
    }

    setHealth({
      overall: issues.length === 0 ? 'good' : issues.some(i => i.severity === 'high') ? 'poor' : 'fair',
      score: Math.max(50, 100 - issues.length * 15),
      trend: issues.length > 0 ? 'declining' : 'stable',
      issues,
    });
  };

  const handleSave = () => {
    onFieldUpdate?.({
      cropType,
      plantingDate,
    });
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'bg-red-500/10 text-red-600 border-red-500/20';
      case 'medium':
        return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20';
      default:
        return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
    }
  };

  const getHealthColor = (overall: string) => {
    switch (overall) {
      case 'excellent':
        return 'text-green-600';
      case 'good':
        return 'text-green-500';
      case 'fair':
        return 'text-yellow-600';
      default:
        return 'text-red-600';
    }
  };

  return (
    <div className="space-y-4">
      {/* Field Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Map className="h-5 w-5" />
            {t('features.satellite.fieldConfiguration')}
          </CardTitle>
          <CardDescription>{t('features.satellite.configureCropDetails', { name: field.name })}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cropType">{t('features.satellite.cropType')}</Label>
              <Select value={cropType} onValueChange={setCropType}>
                <SelectTrigger id="cropType">
                  <SelectValue placeholder="Select crop type" />
                </SelectTrigger>
                <SelectContent>
                  {CROP_TYPES.map((crop) => (
                    <SelectItem key={crop} value={crop}>
                      {crop}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="plantingDate">{t('features.satellite.plantingDate')}</Label>
              <Input
                id="plantingDate"
                type="date"
                value={plantingDate}
                onChange={(e) => setPlantingDate(e.target.value)}
              />
            </div>
          </div>

          <Button onClick={handleSave} className="w-full md:w-auto">
            {t('features.satellite.saveConfiguration')}
          </Button>
        </CardContent>
      </Card>

      {/* Growth Stage */}
      {plantingDate && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sprout className="h-5 w-5" />
              {t('features.satellite.growthStage')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                {GROWTH_STAGES.map((stage, index) => (
                  <div key={stage.id} className="flex items-center">
                    <div
                      className={cn(
                        'flex flex-col items-center gap-2',
                        stage.id === currentStage && 'scale-110'
                      )}
                    >
                      <div
                        className={cn(
                          'w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all',
                          stage.id <= currentStage
                            ? 'bg-primary border-primary text-primary-foreground'
                            : 'bg-muted border-border text-muted-foreground'
                        )}
                      >
                        {stage.id <= currentStage ? (
                          <CheckCircle className="h-5 w-5" />
                        ) : (
                          <span className="text-sm font-semibold">{stage.id}</span>
                        )}
                      </div>
                      <div className="text-xs text-center hidden md:block">
                        <div className="font-medium">{stage.name}</div>
                        <div className="text-muted-foreground">{stage.days}</div>
                      </div>
                    </div>
                    {index < GROWTH_STAGES.length - 1 && (
                      <div
                        className={cn(
                          'h-0.5 w-8 md:w-16 mx-1',
                          stage.id < currentStage ? 'bg-primary' : 'bg-border'
                        )}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Crop Health & Alerts */}
      <Card>
        <CardHeader>
          <CardTitle>{t('features.satellite.cropHealthAlerts')}</CardTitle>
          <CardDescription>{t('features.satellite.aiAnomalyDetection')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Overall Health */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
            <div>
              <div className="text-sm text-muted-foreground">{t('features.satellite.overallHealth')}</div>
              <div className={cn('text-2xl font-bold capitalize', getHealthColor(health.overall))}>
                {health.overall}
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-foreground">{health.score}</div>
              <div className="text-sm text-muted-foreground">{t('features.satellite.healthScore')}</div>
            </div>
          </div>

          {/* Issues */}
          {health.issues.length > 0 ? (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                {t('features.satellite.detectedIssues')} ({health.issues.length})
              </h4>
              {health.issues.map((issue, index) => (
                <Alert key={index} className={cn('border', getSeverityColor(issue.severity))}>
                  <AlertDescription>
                    <div className="space-y-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <Badge variant="outline" className="mb-2 capitalize">
                            {issue.type}
                          </Badge>
                          <p className="text-sm font-medium">{issue.description}</p>
                        </div>
                        <Badge variant="secondary" className="capitalize">
                          {issue.severity}
                        </Badge>
                      </div>
                      <div className="flex items-start gap-2 text-xs text-muted-foreground">
                        <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        <span>{issue.recommendation}</span>
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              ))}
            </div>
          ) : (
            <Alert className="border-green-500/20 bg-green-500/10">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-600">
                {t('features.satellite.noIssuesDetected')}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
